'use client';

import { useEffect, useState } from 'react';

import { Check, Copy, Maximize2 } from 'lucide-react';

import { Segmented } from '../../components/ui/segmented';
import type { WidgetConfigState } from '../fields';
import type { WidgetSchema } from '../types';
import { buildEmbedCode, supportsScriptEmbed, type EmbedFormat } from '../embed/buildEmbedCode';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';

const FORMAT_OPTIONS = [
    { value: 'script', label: 'Script' },
    { value: 'iframe', label: 'iFrame' },
];

export function EmbedCodeBlock({ schema, config }: { schema: WidgetSchema; config: WidgetConfigState }) {
    // Default to the simpler, more robust iframe embed (Polymarket-style); the
    // inline pill needs the script loader for in-text auto-resizing.
    const hasScriptEmbed = supportsScriptEmbed(schema);
    const [format, setFormat] = useState<EmbedFormat>(
        hasScriptEmbed && schema.embed.fixed.widgetSize === 'inline' ? 'script' : 'iframe',
    );
    const [origin, setOrigin] = useState('https://YOUR_WIDGETS_HOST');
    const { copy, copied } = useCopyToClipboard();

    useEffect(() => {
        setOrigin(window.location.origin);
    }, []);

    const code = buildEmbedCode(schema, config, format, origin);

    return (
        <section>
            <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <p className="typ-overline-x10 text-text-x30">Embed code</p>
                    {hasScriptEmbed && (
                        <Segmented
                            value={format}
                            onValueChange={(value) => setFormat(value as EmbedFormat)}
                            options={FORMAT_OPTIONS}
                            aria-label="Embed format"
                        />
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {schema.fullscreenUrl ? (
                        <a
                            href={schema.fullscreenUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-stroke-x30 px-4 text-xs font-medium text-text-x20 transition-colors hover:border-text-x30 hover:text-text-x10"
                        >
                            <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
                            Open fullscreen
                        </a>
                    ) : null}
                    <button
                        type="button"
                        onClick={() => copy(code)}
                        className="inline-flex h-8 items-center gap-1.5 rounded-full bg-text-x10 px-4 text-xs font-medium text-white transition-opacity hover:opacity-85"
                    >
                        {copied ? (
                            <Check className="h-3.5 w-3.5" aria-hidden="true" />
                        ) : (
                            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {copied ? 'Copied' : 'Copy code'}
                    </button>
                </div>
            </div>
            <pre className="max-h-72 overflow-auto rounded-[16px] border border-stroke-x30 bg-[#f5f5f3] p-5 font-mono text-[12.5px] leading-relaxed text-text-x10">
                <code>{code}</code>
            </pre>
        </section>
    );
}
